// AI drafting prompt — shared between the worker (API path uses this with
// tool-use) and the client (manual path uses this verbatim, asking the user
// to paste the response from claude.ai chat back into the modal).

import type { AIDraftResult } from './types';

export const AI_DRAFT_SYSTEM_PROMPT = `You are an assistant for Henry Sheahan Productions ("Sheahan Studios"), a video production company. Henry runs kick-off calls with clients to plan upcoming videos. Your job is to read a transcript of one of those calls and produce structured drafts for each video discussed.

You always pick the single most appropriate template per video from this list:

- brand_film: narrative, story-led brand films (typical run-time 60-90s). Hook → emotional arc → brand message → CTA. Held shots, atmospheric pacing.
- testimonial: interview-led case study. Subject intro → problem → solution → outcome → quotable soundbite. Includes interview questions to ask the subject.
- explainer: information-dense product or service explainer. Problem → solution → features → proof → CTA.
- social_short: hook-first vertical short ad (15-60s). Tight: 3-second hook, body, payoff, CTA. For TikTok / Reels / YouTube Shorts.
- fb_ad_campaign: multi-variant Facebook ad campaign. Same product/service, multiple angles tested in parallel. Use variants[] to express each angle.

For each video, produce body rows where each row is one beat of the script: an "audio" string (what the viewer hears — voiceover line, dialogue, sync sound description) paired with a "visuals" string (what the viewer sees — shot description, B-roll cue, on-screen text). Aim for 4-8 rows per video.

If the transcript mentions specific lines, soundbites, or visual references, weave them in. Otherwise produce a sensible first-draft skeleton tailored to what the client described.

Template-specific fields you should populate when relevant:
- testimonial: subject (interviewee name + role), interview_questions (4-6 questions tailored to the topic)
- social_short: platform (TikTok / Reels / Shorts / etc.), duration ("15s", "30s", "60s")
- fb_ad_campaign: campaign_objective, audience, variants[] (3 variants with distinct angles)`;

const MANUAL_OUTPUT_INSTRUCTIONS = `Return your response as a single JSON object inside a fenced code block, with this exact shape:

\`\`\`json
{
  "client_name": "string",
  "videos": [
    {
      "title": "string",
      "template_key": "brand_film" | "testimonial" | "explainer" | "social_short" | "fb_ad_campaign",
      "overview": "string",
      "objective": "string",
      "rows": [{"audio": "string", "visuals": "string"}],
      "subject": "string (testimonial only)",
      "platform": "string (social_short only)",
      "duration": "string (social_short only)",
      "audience": "string (fb_ad_campaign only)",
      "campaign_objective": "string (fb_ad_campaign only)",
      "interview_questions": ["string (testimonial only)"],
      "variants": [{"name": "string", "rows": [{"audio": "string", "visuals": "string"}]}]
    }
  ]
}
\`\`\`

Output ONLY the JSON code block. No prose before or after. Omit any optional fields that don't apply to the chosen template.`;

/**
 * Build the full prompt the user pastes into Claude.ai chat (manual mode).
 * Combines the system instructions, output format requirements, and the
 * transcript into one self-contained message.
 */
export function buildManualPrompt(transcript: string): string {
  return [
    AI_DRAFT_SYSTEM_PROMPT,
    '',
    MANUAL_OUTPUT_INSTRUCTIONS,
    '',
    '---',
    '',
    'TRANSCRIPT:',
    '',
    transcript.trim(),
  ].join('\n');
}

/**
 * Best-effort extraction of an AIDraftResult JSON from a free-form Claude
 * response. Handles fenced code blocks, raw JSON, and JSON surrounded by
 * prose. Returns null if no usable JSON is found.
 */
export function parseManualResponse(raw: string): AIDraftResult | null {
  if (!raw?.trim()) return null;

  const candidates: string[] = [];

  // Strategy 1: fenced code block (with or without "json" language tag).
  const fenceMatches = [...raw.matchAll(/```(?:json)?\s*([\s\S]*?)```/gi)];
  for (const m of fenceMatches) {
    if (m[1]) candidates.push(m[1].trim());
  }

  // Strategy 2: first { to last } in the whole string.
  const firstBrace = raw.indexOf('{');
  const lastBrace = raw.lastIndexOf('}');
  if (firstBrace >= 0 && lastBrace > firstBrace) {
    candidates.push(raw.slice(firstBrace, lastBrace + 1));
  }

  // Strategy 3: the raw input as-is, in case the user pasted just the JSON.
  candidates.push(raw.trim());

  for (const c of candidates) {
    try {
      const parsed = JSON.parse(c);
      if (
        parsed &&
        typeof parsed.client_name === 'string' &&
        Array.isArray(parsed.videos) &&
        parsed.videos.length > 0
      ) {
        return parsed as AIDraftResult;
      }
    } catch {
      // try next candidate
    }
  }

  return null;
}
